import { useState } from "react"
import { createPortal } from "react-dom"
import type { ClientTheme } from "@/app/lib/themes"

interface HoverTooltipProps {
  /** Tooltip body. Rendered only while hovered/focused; nothing mounts otherwise. */
  content: React.ReactNode
  theme: ClientTheme
  children: React.ReactNode
  maxWidth?: string
}

interface TipPosition {
  top: number
  left: number
  /** Rendered above the trigger by default; flips below near the viewport top. */
  below: boolean
}

/**
 * Lightweight hover/focus tooltip. Portaled to `document.body` (same reasoning
 * as usePopover: the table card's `overflow-hidden` would clip an absolutely
 * positioned tip). Position is captured from the trigger's rect on enter, so
 * there are no effects or listeners — the tip exists only while hovered.
 */
export function HoverTooltip({ content, theme, children, maxWidth = "340px" }: HoverTooltipProps) {
  const [pos, setPos] = useState<TipPosition | null>(null)

  const show = (e: React.SyntheticEvent<HTMLElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    const below = r.top < 140 // cramped above → open downward
    setPos({ top: below ? r.bottom + 8 : r.top - 8, left: r.left + r.width / 2, below })
  }
  const hide = () => setPos(null)

  return (
    <span
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      style={{ display: "inline-flex", minWidth: 0, maxWidth: "100%" }}
    >
      {children}
      {pos &&
        createPortal(
          <div
            role="tooltip"
            className="rounded-lg px-3 py-2"
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              transform: `translate(-50%, ${pos.below ? "0" : "-100%"})`,
              zIndex: 70,
              maxWidth,
              pointerEvents: "none",
              backgroundColor: theme.bgSurface,
              border: `1px solid ${theme.borderColor}`,
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
              fontFamily: "var(--font-ui)",
              fontSize: "12px",
              lineHeight: 1.5,
              color: theme.textPrimary,
              whiteSpace: "pre-wrap",
              overflowWrap: "break-word",
            }}
          >
            {content}
          </div>,
          document.body
        )}
    </span>
  )
}
